<template>
    <div class="account-confirmation">
        <div class="account-confirmation--image">
            <img class="account-confirmation--image--van" src="@/assets/images/decoration/freddies-van.svg" alt="delivery can image">
        </div>
        <h1 class="account-confirmation--title">{{$t('REGISTER_ACCOUNT_CONFIRMATION_TITLE')}}</h1>
        <p v-if="deliveryDate" class="account-confirmation--text" v-html="$t('REGISTER_ACCOUNT_CONFIRMATION_TEXT', {deliveryDate})"/>

        <div class="account-confirmation--post-checkout">
            <p class="account-confirmation--post-checkout--text">{{$t('REGISTER_ACCOUNT_CONFIRMATION_CHECKOUT_TEXT')}}</p>
            <!-- Post checkout questions radio field -->
            <div
                v-for="postCheckoutQuestion in postCheckoutQuestions" :key="postCheckoutQuestion.id"
                class="input__radio">
                <input
                    :id="`post-checkout-question-${postCheckoutQuestion.id}`"
                    name="post-checkout-question"
                    class="account-confirmation--post-checkout--input"
                    type="radio"
                    :value="postCheckoutQuestion.name"
                    v-model="postCheckoutAnswer">
                <label class="account-confirmation--post-checkout--label" :for="`post-checkout-question-${postCheckoutQuestion.id}`">
                    {{ postCheckoutQuestion.name }}
                </label>
            </div>

            <!-- Tiny gap -->
            <div class="clear--xs"></div>

            <router-link :to="{ name: 'account.dashboard' }" class="btn btn--green text-center">
                {{ $t('CONFIRMATION_NEXT_STEP') }}
            </router-link>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { format } from 'date-fns';
import PostCheckoutService from '@/services/users/PostCheckoutService';
import { gaConversion } from '@/helpers/GAHelpers';

export default {
    name: 'AccountConfirmation',
    data() {
        return {
            isFormProcessing: false,
            postCheckoutAnswer: null,
            postCheckoutQuestions: [],
        };
    },
    mounted() {
        this.$store.commit('register/resetState');
        this.getPostCheckoutQuestions();
        this.sendOptimusMetrics();
        this.triggerRokt();

        setTimeout(() => {
            gaConversion('conversion', process.env.VUE_APP_GOOGLE_ANALYTICS_SEND_TO, this.subscription.id);
        }, 2000);
    },
    beforeDestroy() {
        this.closeRokt();
    },
    computed: {
        ...mapGetters({
            userId: 'account/getUserId',
            email: 'account/getEmail',
            couponCode: 'account/getSignupCoupon',
            subscription: 'subscription/getActiveSubscription',
        }),
        deliveryDate() {
            if (this.subscription?.next_delivery_date) return format(new Date(this.subscription?.next_delivery_date), 'EEEE, do LLLL');
            return null;
        },
    },
    methods: {
        triggerRokt() {
            window.rokt && window.rokt.onLoaded((rokt) => {
                rokt.setAttributes({
                    email: this.email,
                    confirmationref: this.userId,
                });

                rokt.triggerPageChange({
                    pageIdentifier: 'confirmation.page',
                });
            });
        },
        closeRokt() {
            window.rokt && window.rokt.onLoaded((rokt) => {
                rokt.closeAll();
            });
        },
        sendOptimusMetrics() {
            const coupon = this.couponCode || null;
            const parsedImpactKey = Number(process.env.VUE_APP_IMPACT_KEY);

            ire('trackConversion', parsedImpactKey, {
                orderId: this.userId,
                orderPromoCode: coupon,
            });

            if (window.AWIN && window.AWIN.Tracking) {
                window.AWIN.Tracking.Sale = {};
                window.AWIN.Tracking.Sale.amount = '1';
                window.AWIN.Tracking.Sale.channel = 'aw';
                window.AWIN.Tracking.Sale.orderRef = this.userId;
                window.AWIN.Tracking.Sale.parts = `DEFAULT:1`;
                window.AWIN.Tracking.Sale.currency = 'GBP';
                window.AWIN.Tracking.Sale.voucher = coupon || '';
                window.AWIN.Tracking.Sale.test = process.env.VUE_APP_ENV === 'Production' ? '0' : '1';
                window.AWIN.Tracking.run();
            }

            let awPixel = new Image(0, 0);
            awPixel.src = `https://www.awin1.com/sread.img?tt=ns&tv=2&merchant=${process.env.VUE_APP_AWIN_KEY}&amount=1&ch=aw&parts=DEFAULT:1&ref=${this.userId}${coupon ? '&vc=' + coupon : '&vc='}&cr=GBP&testmode=0`;
            document.body.appendChild(awPixel);
        },
        async getPostCheckoutQuestions() {
            let postCheckoutService = new PostCheckoutService();
            this.postCheckoutQuestions = await postCheckoutService.getPostCheckoutQuestions();
            this.postCheckoutQuestions.sort(() => Math.random() - 0.5);
        },
        async saveUserPostCheckoutAnswer(postCheckoutAnswer) {
            let postCheckoutService = new PostCheckoutService();
            await postCheckoutService.saveUserPostCheckoutAnswer(postCheckoutAnswer);
        },
    },
    watch: {
        postCheckoutAnswer(value) {
            this.saveUserPostCheckoutAnswer(value);
        },
    },
};
</script>

<style lang="scss" scoped>
.account-confirmation {
    margin: 80px auto;
    width: 308px;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    &--title {
        font-size: 28px;
        line-height: 30px;
        text-align: center;
        color: #262323;
        @media screen and (min-width: 640px) {
            width: 90%;
            font-size: 40px;
            line-height: 48px;
        }
    }
    &--text {
        font-size: 14px;
        line-height: 18px;
        text-align: center;
        color: #262323;
        @media screen and (min-width: 640px) {
            font-size: 16px;
            line-height: 20px;
        }
    }
    &--image {
        width: 70%;
        &--van {
            width: 100%;
        }
    }
    &--post-checkout {
        padding: 20px;
        margin-top: 20px;
        border: 1px solid #e6e6e6;
        &--text, &--label {
            font-size: 15px;
            line-height: 18px;
            font-weight: 200;
            color: #262323;
            @media screen and (min-width: 640px) {
                font-size: 16px;
                line-height: 20px;
            }
        }
        &--text {
            font-weight: 600;
        }
        &--label {
            margin-left: 10px;
            margin-top: 5px;
        }
        &--input {
            font-size: 14px;
            margin-top: 5px;
        }
    }
}
.btn {
    min-width: 150px;
    width: 100%;
}
</style>
